/**
 * frontend/src/pages/valuation/_SignOffGates.tsx
 * REFACTOR-SPLIT (VAL-PATH P6s): 431 -> 199 lines. Not truncation: Finalize
 * moved to _FinalizeCard.tsx, Produce document to _ProduceDocumentCard.tsx.
 *
 * VALUATION-B3 sign-off — the gate wall, and the two actions it guards.
 *
 * The gates are not decoration and they are not the enforcement. Every one of
 * them is also checked server-side: `/finalize` refuses a body the engine would
 * not stand behind, and `can_produce_document` is computed on the snapshot, not
 * here. What this file does is tell the valuator WHY a button is disabled
 * before they press it, because a button that silently does nothing teaches a
 * user to distrust the screen.
 *
 * One gate deserves a note. `preflight.ok` is the same computation `/finalize`
 * will run, but never on the rationale — the rationale changes no number, so
 * typing it does not re-trigger `preflight.ok`. A blank rationale on a tuned
 * methodology is instead a `canFinalize` condition ValuationSignOff.tsx checks
 * directly (`blockedReason`: "Write the reason for this record."); `/finalize`
 * still refuses a blank one strictly, server-side, at the point of freezing.
 *
 * Split out of ValuationSignOff.tsx under docs/FILE_SIZE_POLICY.md.
 */
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ROLE_LABELS } from '@/components/AppShell'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchFinancials } from '@/api/valuationFinancials'
import { stepRole } from './_steps'
import { nextStepPath } from './_shared'
import type { PreflightResponse } from '@/types/valuationB3'
import { offerFromReadings, type AgreementOffer } from './_operatingAgreement'

/** The server's key-people blocker, word for word (core/valuation/company_profile.py KEY_PEOPLE_BLOCK_TEXT). */
export const KEY_PEOPLE_BLOCK_TEXT =
  "Add at least one key person before you finalize. The operating agreement doesn't name anyone, so enter their first name, last name, role and phone or email."

/** WALKFIX-B F16 (WF-B-4, owner approved 2026-10-25 11:15): the same gate when no agreement was read. */
export const NO_KEY_PEOPLE_TEXT =
  'Add at least one key person before you finalize: enter their first name, last name, role and phone or email.'

/** True only when an operating agreement was read and named no one — the one case the agreement sentence is true. */
export function namedNobody(offer: AgreementOffer): boolean {
  return offer.readFiles.length > 0 && offer.owners.length === 0 && offer.people.length === 0
}

/** `namedNobody` for this engagement's stored readings; fetched only while the key-people gate shows. A failed fetch reads false. */
export function useAgreementNamedNobody(engagementId: string | undefined, enabled: boolean): boolean {
  const [nobody, setNobody] = useState(false)
  useEffect(() => {
    if (!engagementId || !enabled) return
    let live = true
    fetchFinancials(engagementId)
      .then(view => { if (live) setNobody(namedNobody(offerFromReadings(view.operating_agreements))) })
      .catch(() => { if (live) setNobody(false) })
    return () => { live = false }
  }, [engagementId, enabled])
  return nobody
}

/** One gate row: satisfied, blocked, or not determinable from this session. */
export function Gate({
  state,
  label,
  detail,
}: {
  state: 'ok' | 'blocked' | 'unknown'
  label: string
  detail: string
}) {
  const mark = state === 'ok' ? '✓' : state === 'blocked' ? '!' : '?'
  const tone =
    state === 'ok' ? 'border-green-200 bg-green-50 text-green-900'
      : state === 'blocked' ? 'border-amber-300 bg-amber-50 text-amber-900'
        : 'border-gray-200 bg-gray-50 text-gray-700'
  return (
    <div className={`flex gap-3 rounded-lg border p-3 ${tone}`}>
      <span className="font-bold" aria-hidden>{mark}</span>
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="mt-0.5 text-xs opacity-90">{detail}</p>
      </div>
    </div>
  )
}

export function SignOffGates({
  hasComputedValue,
  concluded,
  canConclude,
  userName,
  userRole,
  hasTuningInputs,
  preflight,
  preflightError,
  onBack,
  liveVersion = null,
  engagementId,
  agreementNamedNobody = false,
}: {
  /** True once the engine has produced a band for the inputs in this
   *  session (`preflight.computed`) — the same body Finalize will freeze.
   *  VAL-STATE-PERSIST-1 R1: this is NOT the engagement's persisted band.
   *  Only Finalize writes that column pair, so gating on it here made
   *  Record unreachable — it required the exact thing only Record itself
   *  can produce. */
  hasComputedValue: boolean
  /** True for a conclusion-of-value engagement. False for a calculation one,
   *  where a `valuation_owner` may record without holding `canConclude`. */
  concluded: boolean
  canConclude: boolean
  userName?: string | undefined
  /** Stored role code; shown in words via ROLE_LABELS. */
  userRole?: string | undefined
  hasTuningInputs: boolean
  preflight: PreflightResponse | null
  preflightError: string | null
  /** The step before Sign: Your value for the owner, Methodology and rates for a signer. */
  onBack: () => void
  /** Live (status: 'final') snapshot version, from the caller's own
   *  snapshot-history fetch. `null`/omitted keeps the heading off — this
   *  file does not derive the number itself. */
  liveVersion?: number | null
  /** When set, the key-people item links to this engagement's Company screen. */
  engagementId?: string | undefined
  /** True when an operating agreement was read and names no one (`namedNobody`); otherwise the key-people gate reads WF-B-4. */
  agreementNamedNobody?: boolean
}) {
  const allBlockers = preflight?.blockers ?? []
  const keyPeopleBlocked = allBlockers.includes(KEY_PEOPLE_BLOCK_TEXT)
  const blockers = allBlockers.filter(b => b !== KEY_PEOPLE_BLOCK_TEXT)
  // Key people has its own gate below; when it is the only blocker the engine check itself is clean.
  const engineClean = !!preflight && (preflight.ok || (keyPeopleBlocked && blockers.length === 0))
  const warnings = preflight?.warnings ?? []
  // Never print an email: empty or email-shaped names count as missing.
  const signerName0 = userName && !userName.includes('@') ? userName : 'this account'
  const roleWords = userRole ? (ROLE_LABELS[userRole] ?? '') : ''
  const signerName = roleWords ? `${signerName0} (${roleWords})` : signerName0
  // VAL-PATH-ROUTES (P1): the owner never sees Methodology.
  const owner = stepRole(userRole) === 'owner'

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {liveVersion !== null ? 'To sign again:' : 'Gates'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Gate
          state={hasComputedValue ? 'ok' : 'blocked'}
          label="Value calculated for these inputs"
          detail={hasComputedValue
            ? 'The engine has computed a band for the methodology inputs in this session.'
            : owner
              ? 'Go back to Your value — the engine has not returned a computed band for this session yet.'
              : 'Enter methodology inputs on the Methodology screen — the engine has not returned a computed band for this session yet.'}
        />
        <Gate
          state={concluded ? (canConclude ? 'ok' : 'blocked') : 'ok'}
          label={concluded ? 'Role can finalize' : 'Role can record'}
          detail={concluded
            ? (canConclude
              ? `Signed in as ${signerName} — a conclusion role.`
              : 'A conclusion of value may only be asserted by a credentialed Valuation Signer role. Your role may run calculation engagements.')
            : (canConclude
              ? `Signed in as ${signerName} — recording and signing this calculation.`
              : `Signed in as ${signerName} — recording this estimate.`)}
        />

        {!hasTuningInputs ? (
          <>
            <Gate
              state="blocked"
              label="Inputs saved for this engagement"
              detail={owner
                ? 'Nothing is saved yet — open Questions, then Your value.'
                : 'Nothing is saved yet — open Intake, then Methodology.'}
            />
            <Button variant="outline" size="sm" onClick={onBack}>
              {owner ? 'Back' : 'Back to Methodology'}
            </Button>
          </>
        ) : preflightError ? (
          <Gate
            state="unknown"
            label="Engine check did not return"
            detail={preflightError}
          />
        ) : !preflight ? (
          <Gate
            state="unknown"
            label="Engine check in progress"
            detail="Running the same computation Finalize will run."
          />
        ) : (
          <>
            <Gate
              state={engineClean ? 'ok' : 'blocked'}
              label="Engine check returned"
              detail={engineClean
                ? 'No blockers. The same computation Finalize runs returned clean.'
                : blockers.join(' · ')}
            />
            {keyPeopleBlocked && (
              <>
                <Gate state="blocked" label="Key people entered" detail={agreementNamedNobody ? KEY_PEOPLE_BLOCK_TEXT : NO_KEY_PEOPLE_TEXT} />
                {engagementId && (
                  <Link className="text-sm font-medium text-pal-link underline" to={nextStepPath('profile', engagementId) ?? '/valuation'}>
                    Go to the Company screen
                  </Link>
                )}
              </>
            )}
            {warnings.length > 0 && (
              <Gate
                state="unknown"
                label="Warnings recorded with the signature, not blocking"
                detail={warnings.join(' · ')}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
